import { toNameCase, validatePersonName } from './authValidation'

export type SignupProfileInput = {
  givenName: string
  familyName: string
  dateOfBirth: string
  gender: string
  motherName?: string
  hasMotherName?: 'yes' | 'no'
}

/**
 * Monta os campos de perfil (nome/sobrenome, nascimento, gênero, nome da
 * mãe) do payload de signup, na convenção do projeto: cada campo custom
 * vai duas vezes — chave crua E prefixada com `ulp-` (prefixo que a
 * Universal Login persiste entre telas do fluxo Identifier First e entrega
 * em `event.request.body` na Action Pre User Registration).
 *
 * Nomes saem com a inicial de cada palavra maiúscula (`toNameCase`),
 * qualquer que seja a tela.
 *
 * Usado por useSignupManager (signup single-step) e useSignupIdManager
 * (passo 1 do Identifier First) para manter os dois fluxos em sincronia.
 */
export function buildSignupProfilePayload(
  input: SignupProfileInput,
): Record<string, string> {
  const givenName = toNameCase(input.givenName.trim())
  const familyName = toNameCase(input.familyName.trim())
  const fullName = [givenName, familyName].filter(Boolean).join(' ').trim()
  const dateOfBirth = input.dateOfBirth.trim()
  const gender = input.gender.trim()
  const motherName =
    input.hasMotherName === 'yes' ? toNameCase(input.motherName?.trim() || '') : ''
  const hasMotherName = input.hasMotherName

  return {
    ...(fullName ? { 'ulp-name': fullName, name: fullName } : {}),
    ...(givenName
      ? { 'ulp-given_name': givenName, given_name: givenName }
      : {}),
    ...(familyName
      ? { 'ulp-family_name': familyName, family_name: familyName }
      : {}),
    ...(dateOfBirth
      ? { 'ulp-date_of_birth': dateOfBirth, date_of_birth: dateOfBirth }
      : {}),
    ...(gender ? { 'ulp-gender': gender, gender } : {}),
    ...(motherName
      ? { 'ulp-mother_name': motherName, mother_name: motherName }
      : {}),
    ...(hasMotherName
      ? {
          'ulp-has_mother_name': hasMotherName === 'yes' ? 'true' : 'false',
          has_mother_name: hasMotherName === 'yes' ? 'true' : 'false',
        }
      : {}),
  }
}

export function validateSignupProfile(input: SignupProfileInput): void {
  const nameError =
    validatePersonName(input.givenName, 'givenName') ??
    validatePersonName(input.familyName, 'familyName')
  if (nameError) throw new Error(nameError)
  if (!input.dateOfBirth.trim()) throw new Error('DATE_OF_BIRTH_REQUIRED')
  if (!input.gender.trim()) throw new Error('GENDER_REQUIRED')
  const motherError = validatePersonName(input.motherName ?? '', 'motherName')
  if (motherError) throw new Error(motherError)
}

/**
 * Mesma regra usada em auth0/actions/pre-user-registration-onboarding.js
 * (api.access.deny): telefone é obrigatório e precisa normalizar pra 10-11
 * dígitos nacionais. Validar aqui evita o usuário bater no erro genérico da
 * Action sem contexto.
 */
export function normalizePhoneOrThrow(rawPhone: string | undefined): string {
  const digits = (rawPhone || '').replace(/\D/g, '')
  const national =
    digits.startsWith('55') && digits.length >= 12 ? digits.slice(2) : digits
  if (national.length < 10 || national.length > 11) {
    throw new Error('PHONE_INVALID')
  }
  return `+55${national}`
}
